import emailjs from '@emailjs/browser';
import { sanitizeEmail, sanitizeFormData, validateFormData } from './inputSanitizer';

// EmailJS Configuration
// Replace these with your EmailJS credentials
// Get them from: https://www.emailjs.com/
// See EMAILJS_SETUP.md for detailed setup instructions
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
const EMAILJS_TEMPLATE_ID_QUOTE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_QUOTE || 'YOUR_QUOTE_TEMPLATE_ID';
const EMAILJS_TEMPLATE_ID_CONTACT = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CONTACT || 'YOUR_CONTACT_TEMPLATE_ID';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

// Default recipient email - change this to your desired email address
const DEFAULT_RECIPIENT_EMAIL = 'info@bigstridesinc.com';

// Initialize EmailJS
if (EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY') {
  emailjs.init(EMAILJS_PUBLIC_KEY);
}

/**
 * Send quote request email
 * @param {Object} formData - Form data containing name, email, phone, company, partdesc
 * @param {string} recipientEmail - Email address to send the email to (optional, defaults to DEFAULT_RECIPIENT_EMAIL)
 * @returns {Promise} EmailJS send promise
 */
export const sendQuoteRequestEmail = async (formData, recipientEmail = DEFAULT_RECIPIENT_EMAIL) => {
  // Check if EmailJS is configured
  if (EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID' || EMAILJS_PUBLIC_KEY === 'YOUR_PUBLIC_KEY') {
    throw new Error('EmailJS is not configured. Please set up your environment variables. See EMAILJS_SETUP.md for instructions.');
  }

  // Sanitize and validate form data
  const sanitized = sanitizeFormData(formData);
  const validation = validateFormData(sanitized, ['name', 'email', 'partdesc']);

  if (!validation.isValid) {
    throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
  }

  // Ensure recipient email is sanitized
  const sanitizedRecipient = sanitizeEmail(recipientEmail) || DEFAULT_RECIPIENT_EMAIL;

  const templateParams = {
    to_email: sanitizedRecipient,
    from_name: sanitized.name || '',
    from_email: sanitized.email || '',
    phone: sanitized.phone || 'Not provided',
    company: sanitized.company || 'Not provided',
    part_description: sanitized.partdesc || '',
    reply_to: sanitized.email || '',
  };

  try {
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_QUOTE,
      templateParams
    );
    return { success: true, response };
  } catch (error) {
    console.error('EmailJS Error:', error);
    throw error;
  }
};

/**
 * Send contact form email
 * @param {Object} formData - Form data containing name, email, phone, company, message
 * @param {string} recipientEmail - Email address to send the email to (optional, defaults to DEFAULT_RECIPIENT_EMAIL)
 * @returns {Promise} EmailJS send promise
 */
export const sendContactEmail = async (formData, recipientEmail = DEFAULT_RECIPIENT_EMAIL) => {
  // Check if EmailJS is configured
  if (EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID' || EMAILJS_PUBLIC_KEY === 'YOUR_PUBLIC_KEY') {
    throw new Error('EmailJS is not configured. Please set up your environment variables. See EMAILJS_SETUP.md for instructions.');
  }

  // Sanitize and validate form data
  const sanitized = sanitizeFormData(formData);
  const validation = validateFormData(sanitized, ['name', 'email', 'message']);

  if (!validation.isValid) {
    throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
  }

  // Ensure recipient email is sanitized
  const sanitizedRecipient = sanitizeEmail(recipientEmail) || DEFAULT_RECIPIENT_EMAIL;

  const templateParams = {
    to_email: sanitizedRecipient,
    from_name: sanitized.name || '',
    from_email: sanitized.email || '',
    phone: sanitized.phone || 'Not provided',
    company: sanitized.company || 'Not provided',
    message: sanitized.message || '',
    reply_to: sanitized.email || '',
  };

  try {
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_CONTACT,
      templateParams
    );
    return { success: true, response };
  } catch (error) {
    console.error('EmailJS Error:', error);
    throw error;
  }
};

